import React, { useState, useEffect, useMemo } from "react";
import "../App.css";
import { apiRequest, hasRole, API_ENDPOINTS, getCurrentUser } from "../utils/api";
import { useTranslation } from "react-i18next";

export default function OwnerDashboard() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // Data states
    const [dashboardData, setDashboardData] = useState(null);
    const [apartments, setApartments] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [financials, setFinancials] = useState([]);
    const [payments, setPayments] = useState([]);
    /** When set, "Rental Income by Period" lists only this apartment's rows (toggle off by clicking again). */
    const [selectedApartmentId, setSelectedApartmentId] = useState(null);

    const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";
    const currentUser = getCurrentUser();
    const { t } = useTranslation();

    const filteredFinancials = useMemo(() => {
        if (selectedApartmentId == null) return financials;
        return financials.filter((f) => f.apartment_id === selectedApartmentId);
    }, [financials, selectedApartmentId]);

    const selectedApartment = useMemo(
        () => apartments.find((a) => a.id === selectedApartmentId),
        [apartments, selectedApartmentId]
    );

    /** Recent invoices: all when no filter; when an apartment is selected, all invoices for that apartment (full list). */
    const filteredInvoicesForSection = useMemo(() => {
        if (!dashboardData) return [];
        if (selectedApartmentId == null) {
            return dashboardData.recent_invoices || [];
        }
        return invoices.filter((inv) => inv.apartment_id === selectedApartmentId);
    }, [dashboardData, invoices, selectedApartmentId]);

    const handleApartmentCardClick = (apartment) => {
        setSelectedApartmentId((prev) => (prev === apartment.id ? null : apartment.id));
    };

    const handleApartmentCardKeyDown = (e, apartment) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleApartmentCardClick(apartment);
        }
    };

    // Check if user is owner
    useEffect(() => {
        if (!hasRole("owner")) {
            window.location.href = "/home";
            return;
        }
        loadOwnerData();
    }, []);

    const loadOwnerData = async () => {
        setLoading(true);
        setError("");

        try {
            const [dashboardData, apartmentsData, invoicesData, financialsData, paymentsData] = await Promise.all([
                apiRequest(API_ENDPOINTS.OWNER_DASHBOARD),
                apiRequest(API_ENDPOINTS.OWNER_APARTMENTS),
                apiRequest(API_ENDPOINTS.OWNER_INVOICES),
                apiRequest(API_ENDPOINTS.OWNER_FINANCIALS),
                apiRequest(API_ENDPOINTS.OWNER_SPECIAL_PAYMENTS)
            ]);

            setDashboardData(dashboardData);
            setApartments(apartmentsData);
            setInvoices(invoicesData);
            setFinancials(financialsData);
            setPayments(paymentsData);
        } catch (err) {
            console.error("Error loading owner data:", err);
            setError("Failed to load dashboard data");
        } finally {
            setLoading(false);
        }
    };

    if (loading && !dashboardData) {
        return (
            <div className="owner-dashboard">
                <div className="loading">{t("owner.loading")}</div>
            </div>
        );
    }

    return (
        <div className="owner-dashboard">
            <div className="owner-header">
                <h1>
                    {t("owner.headerTitle", {
                        username:
                            currentUser && currentUser.username
                                ? currentUser.username
                                : t("owner.fallbackName"),
                    })}
                </h1>
                <p>{t("owner.headerSubtitle")}</p>
            </div>

            {error && <div className="alert error">{error}</div>}
            {success && <div className="alert success">{success}</div>}

            {dashboardData && (
                <div className="owner-content">
                    {/* Statistics Overview */}
                    <div className="overview-grid">
                        <div className="stat-card">
                            <h3>{t("owner.myApartmentsCardTitle")}</h3>
                            <div className="stat-number">{dashboardData.apartments.total_apartments}</div>
                            <div className="stat-details owner-status-summary">
                                <div className="owner-status-item">
                                    <span className="status-dot status-red" />
                                    <span className="status-label">
                                        {t("owner.rentedLabel")}:{" "}
                                        <strong>{dashboardData.apartments.rented_count}</strong>
                                    </span>
                                </div>
                                <div className="owner-status-item">
                                    <span className="status-dot status-green" />
                                    <span className="status-label">
                                        {t("owner.availableLabel")}:{" "}
                                        <strong>{dashboardData.apartments.available_count}</strong>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <h3>{t("owner.totalIncomeTitle")}</h3>
                            <div className="stat-number">${dashboardData.apartments.total_income ?? 0}</div>
                            {/* <div className="stat-details">
                                <span>{t("owner.totalIncomeSubtitle")}</span>
                            </div> */}
                        </div>

                        <div className="stat-card">
                            <h3>{t("owner.recentActivityTitle")}</h3>
                            <div className="stat-number">{dashboardData.recent_invoices.length}</div>
                            <div className="stat-details">
                                <span>{t("owner.recentActivitySubtitle")}</span>
                            </div>
                        </div>
                    </div>


                    {/* My Apartments — click to filter "Rental Income by Period" */}
                    <div className="apartments-section owner-apartments-filter">
                        <h2>{t("owner.myApartmentsSectionTitle")}</h2>
                        <p className="section-description owner-apartment-filter-hint">
                            {t("owner.apartmentFilterHint")}
                        </p>
                        <div className="apartments-grid">
                            {apartments.map(apartment => {
                                const status = (apartment.status || "").toLowerCase();
                                const isFree = status === "empty" || status === "available" || status === "";
                                const isSelected = selectedApartmentId === apartment.id;
                                const numLabel = apartment.number || "—";
                                return (
                                    <div
                                        key={apartment.id}
                                        role="button"
                                        tabIndex={0}
                                        className={`apartment-card icon-card apartment-card--selectable${isSelected ? " is-selected" : ""}`}
                                        onClick={() => handleApartmentCardClick(apartment)}
                                        onKeyDown={e => handleApartmentCardKeyDown(e, apartment)}
                                        aria-pressed={isSelected}
                                        aria-label={t("owner.filterApartmentAria", { number: numLabel })}
                                    >
                                        <div className="icon" aria-hidden="true">
                                            🏠
                                        </div>
                                        <div className="apartment-info">
                                            <div className="apartment-number-row">
                                                <span className="apartment-number">{numLabel}</span>
                                                <span
                                                    className={`status-dot ${isFree ? "status-green" : "status-red"}`}
                                                />
                                            </div>
                                            <span className="apartment-layout">
                                                {apartment.layout || (apartment.type || "").trim() || "N/A"}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Financial Summary */}
                    <div className="financials-section">
                        <h2>{t("owner.financialsSectionTitle")}</h2>
                        {selectedApartmentId != null && (
                            <div className="financials-filter-banner">
                                <span className="financials-filter-banner__text">
                                    {t("owner.financialsFilteredFor", {
                                        number: selectedApartment?.number || selectedApartmentId,
                                    })}
                                </span>
                                <button
                                    type="button"
                                    className="btn-financials-show-all"
                                    onClick={() => setSelectedApartmentId(null)}
                                >
                                    {t("owner.financialsShowAllApartments")}
                                </button>
                            </div>
                        )}
                        <p className="section-description">
                            {t("owner.financialsSectionSubtitle")}
                        </p>
                        {filteredFinancials.length > 0 ? (
                            <div className="financials-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>{t("owner.financialsTable.apartment")}</th>
                                            <th>{t("owner.financialsTable.tenant")}</th>
                                            <th>{t("owner.financialsTable.period")}</th>
                                            <th>{t("owner.financialsTable.totalIncome")}</th>
                                            <th>{t("owner.financialsTable.expenses")}</th>
                                            <th>{t("owner.financialsTable.attachment")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredFinancials.map(financial => (
                                            <tr
                                                key={
                                                    financial.id ??
                                                    `${financial.apartment_id}-${financial.created_at}-${financial.checkin_date}`
                                                }
                                            >
                                                <td>
                                                    <div className="apartment-info">
                                                        <strong>#{financial.apartment_number}</strong>
                                                        <small>ID: {financial.apartment_id}</small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="tenant-info">
                                                        <strong>{financial.tenant_name || "—"}</strong>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="period-info">
                                                        <div className="checkin-date">
                                                            <strong>{t("owner.financialsCheckIn")}</strong>{" "}
                                                            {financial.checkin_date
                                                                ? new Date(financial.checkin_date).toLocaleDateString()
                                                                : "—"}
                                                        </div>
                                                        <div className="checkout-date">
                                                            <strong>{t("owner.financialsCheckOut")}</strong>{" "}
                                                            {financial.checkout_date
                                                                ? new Date(financial.checkout_date).toLocaleDateString()
                                                                : t("owner.financialsActive")}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="income-amount">
                                                        <strong>
                                                            {financial.currency || "$"}
                                                            {parseFloat(financial.total_income ?? 0).toLocaleString()}
                                                        </strong>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="financial-expenses-cell">
                                                        <strong>
                                                            {financial.currency || "$"}
                                                            {parseFloat(financial.expenses ?? 0).toLocaleString()}
                                                        </strong>
                                                    </div>
                                                </td>
                                                <td>
                                                    {financial.proof ? (
                                                        <a
                                                            href={`${API_BASE}/uploads/${financial.proof}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="proof-link"
                                                        >
                                                            {t("owner.financialsView")}
                                                        </a>
                                                    ) : (
                                                        <span className="no-proof">
                                                            {t("owner.financialsNoFile")}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : financials.length > 0 ? (
                            <div className="no-data">
                                <p>{t("owner.financialsEmptyForApartment")}</p>
                            </div>
                        ) : (
                            <div className="no-data">
                                <h3>{t("owner.financialsEmpty")}</h3>
                            </div>
                        )}
                    </div>

                    {/* Recent Invoices (filtered by apartment when a card is selected) */}
                    <div className="invoices-section">
                        <h2>{t("owner.recentInvoicesSectionTitle")}</h2>
                        {selectedApartmentId != null && (
                            <p className="section-description owner-invoices-filter-note">
                                {t("owner.recentInvoicesFilteredFor", {
                                    number: selectedApartment?.number || selectedApartmentId,
                                })}
                            </p>
                        )}
                        {filteredInvoicesForSection.length > 0 ? (
                            <div className="invoices-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>{t("owner.recentInvoicesTable.apartment")}</th>
                                            <th>{t("owner.recentInvoicesTable.item")}</th>
                                            <th>{t("owner.recentInvoicesTable.amount")}</th>
                                            <th>{t("owner.recentInvoicesTable.date")}</th>
                                            <th>{t("owner.recentInvoicesTable.attachment")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredInvoicesForSection.map(invoice => (
                                            <tr key={invoice.id}>
                                                <td>{invoice.apartment_number}</td>
                                                <td>{invoice.item}</td>
                                                <td>${invoice.total}</td>
                                                <td className="cell-nowrap">{new Date(invoice.created_at).toLocaleDateString()}</td>
                                                <td>
                                                    {invoice.proof ? (
                                                        <a
                                                            href={`${API_BASE}/uploads/${invoice.proof}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="proof-link"
                                                        >
                                                            {t("owner.recentInvoicesView")}
                                                        </a>
                                                    ) : (
                                                        <span className="no-proof">
                                                            {t("owner.recentInvoicesNoFile")}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : selectedApartmentId != null ? (
                            <div className="no-data">
                                <p>{t("owner.recentInvoicesEmptyForApartment")}</p>
                            </div>
                        ) : (
                            <div className="no-data">
                                <p>{t("owner.recentInvoicesEmpty")}</p>
                            </div>
                        )}
                    </div>

                    {/* Admin-recorded payouts to owner */}
                    <div className="owner-payments-section">
                        <h2>{t("owner.paymentsTitle")}</h2>
                        <p className="section-description">
                            {t("owner.paymentsDescription")}
                        </p>
                        {payments.length > 0 ? (
                            <div className="invoices-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>{t("owner.paymentsTable.paidAt")}</th>
                                            <th>{t("owner.paymentsTable.amount")}</th>
                                            <th>{t("owner.paymentsTable.description")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.map(sp => (
                                            <tr key={sp.id}>
                                                <td className="cell-nowrap">{sp.paid_at ? new Date(sp.paid_at).toLocaleDateString() : "-"}</td>
                                                <td>${Number(sp.amount).toLocaleString()}</td>
                                                <td>{sp.description || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="no-data">
                                <p>{t("owner.paymentsEmpty")}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
